import React, { useMemo, useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { gbp } from "../lib/format";
import { PlusCircle, Tag } from "lucide-react";

const shortWallet = (str) => {
  if (!str) return "0x…";
  const s = str.trim();
  if (s.length <= 10) return s;
  return `${s.slice(0, 5)}…${s.slice(-4)}`;
};

export const SellFractionForm = ({ properties, onListingAdded }) => {
  const [propertyId, setPropertyId] = useState(properties?.[0]?.id || "");
  const [tokens, setTokens] = useState(5000);
  const [pricePerToken, setPricePerToken] = useState(1.0);
  const [wallet, setWallet] = useState("0x8Fa2…C9d1");
  const [submitting, setSubmitting] = useState(false);

  const selected = useMemo(
    () => properties?.find((p) => p.id === propertyId),
    [properties, propertyId]
  );

  const totalPrice = useMemo(() => Number(tokens) * Number(pricePerToken), [tokens, pricePerToken]);
  const variance = useMemo(() => Math.round((pricePerToken - 1) * 100), [pricePerToken]);

  const varianceChip = (v) => {
    if (v === 0) return { label: "Pari Passu", color: "#334155", bg: "#F1F5F9" };
    if (v > 0) return { label: `+${v}% Premium`, color: "#065F46", bg: "#D1FAE5" };
    return { label: `${v}% Discount`, color: "#8B6F1D", bg: "rgba(212,175,55,0.14)" };
  };
  const chip = varianceChip(variance);

  const canSubmit = propertyId && tokens >= 500 && pricePerToken > 0 && wallet.trim().length >= 6;

  const handleSubmit = () => {
    if (!selected) return toast.error("Please pick a property");
    if (tokens < 500) return toast.error("Minimum listing size is 500 BSTK");
    if (pricePerToken <= 0) return toast.error("Price per token must be positive");
    setSubmitting(true);
    const newListing = {
      id: `local-${Date.now()}`,
      property_id: propertyId,
      property_name: selected.name,
      property_city: selected.city,
      property_asset_type: selected.asset_type,
      property_yield_pct: selected.projected_yield_pct,
      tokens: Number(tokens),
      price_per_token_gbp: Number(pricePerToken),
      price_variance_pct: variance,
      total_price_gbp: Number(totalPrice.toFixed(2)),
      seller_wallet: shortWallet(wallet),
      status: "open",
      is_local: true,
    };
    setTimeout(() => {
      onListingAdded?.(newListing);
      toast.success(`Listed ${newListing.tokens.toLocaleString()} BSTK of ${selected.name}.`);
      setSubmitting(false);
    }, 300);
  };

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="sell-fraction-form"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
        <div>
          <div className="overline flex items-center gap-2">
            <Tag className="h-3 w-3 brickstok-gold" /> List a Position
          </div>
          <h3 className="mt-1 font-display text-xl font-semibold tracking-tight text-slate-900">
            Sell fractions on the notice board
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Offer part of your holding to other Brickstok members. Priced against par (£1 / token).
          </p>
        </div>
      </div>

      <div className="grid gap-5 p-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <Label className="text-sm font-medium text-slate-700">Asset</Label>
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="mt-2 h-11" data-testid="sell-property-select">
              <SelectValue placeholder="Select a property" />
            </SelectTrigger>
            <SelectContent>
              {properties?.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`sell-property-option-${p.id}`}>
                  {p.name} · {p.city} · {p.asset_type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="sell-tokens" className="text-sm font-medium text-slate-700">
            Fractions for sale <span className="text-xs text-slate-500">(BSTK)</span>
          </Label>
          <div className="relative mt-2">
            <Input
              id="sell-tokens"
              type="number"
              min={500}
              step={100}
              value={tokens}
              onChange={(e) => setTokens(Math.max(0, Number(e.target.value) || 0))}
              className="h-11 pr-16 font-mono-num text-lg font-semibold text-slate-900"
              data-testid="sell-tokens-input"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">BSTK</span>
          </div>
          <div className="mt-1 text-xs text-slate-500">Minimum 500 BSTK</div>
        </div>

        <div>
          <Label htmlFor="sell-price" className="text-sm font-medium text-slate-700">
            Price per token
          </Label>
          <div className="relative mt-2">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-mono-num">£</span>
            <Input
              id="sell-price"
              type="number"
              min={0}
              step={0.01}
              value={pricePerToken}
              onChange={(e) => setPricePerToken(Math.max(0, Number(e.target.value) || 0))}
              className="h-11 pl-7 font-mono-num text-lg font-semibold text-slate-900"
              data-testid="sell-price-input"
            />
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs">
            <span
              className="rounded-sm px-2 py-0.5 font-semibold uppercase tracking-widest"
              style={{ background: chip.bg, color: chip.color }}
              data-testid="sell-variance-chip"
            >
              {chip.label}
            </span>
            <span className="text-slate-500 font-mono-num">vs. par</span>
          </div>
        </div>

        <div className="md:col-span-2">
          <Label htmlFor="sell-wallet" className="text-sm font-medium text-slate-700">
            Your wallet address
          </Label>
          <Input
            id="sell-wallet"
            type="text"
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            className="mt-2 h-11 font-mono text-sm"
            placeholder="0x…"
            data-testid="sell-wallet-input"
          />
        </div>

        <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 rounded-md border border-slate-200 bg-slate-50 px-5 py-4">
          <div>
            <div className="overline">Total listing value</div>
            <div className="mt-1 font-display text-2xl font-semibold text-slate-900 font-mono-num" data-testid="sell-total-preview">
              {gbp(totalPrice, { decimals: 2 })}
            </div>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="text-slate-900 hover:brightness-95"
            style={{ background: "#D4AF37" }}
            data-testid="sell-submit-btn"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            {submitting ? "Listing…" : "Publish to Notice Board"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SellFractionForm;
